import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { PROMOTION_CAMPAIGNS_QUERY_KEY } from "@/constants";

import { useSocket } from "./useSocket";

// Keeps the cached campaign feed honest while a page is open.
//
// A campaign is not a player's own data: an admin changes one in the bonus
// panel (a new banner image, a renamed offer, a different schedule), the
// backend re-pulls its mirror and broadcasts `promotion:campaigns` to every
// connected socket. Queries are cached for five minutes
// (lib/queryClient.ts), so without this a player sitting on the promotions
// page or the lobby keeps seeing the old image until they navigate away.
//
// Mounted once, app-wide (components/Core/PromotionSync), because the feed is
// shown on two surfaces. The socket only exists for a signed-in player; a
// visitor gets the new data from the refetch on mount instead.
export const usePromotionCampaignSync = (): void => {
  const socket = useSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket) return;

    const handleCampaignsUpdate = () =>
      queryClient.invalidateQueries({ queryKey: PROMOTION_CAMPAIGNS_QUERY_KEY });

    socket.on("promotion:campaigns", handleCampaignsUpdate);
    return () => {
      socket.off("promotion:campaigns", handleCampaignsUpdate);
    };
  }, [socket, queryClient]);
};
